/**
 * Shared system-feedback layer — spec 17 "System Feedback" + INTERACTIONS.md L25.
 *
 * ONE field-state contract, spoken by every validating surface in the theme, so
 * a single CSS system covers newsletter, auth, contact, coupon and pincode:
 *
 *   invalid → `kashira-field-invalid` on the control AND on the element that
 *             actually draws its border (Dawn's `.field` / `.select` wrapper,
 *             the pincode row), `kashira-shake` for 300ms on that same border
 *             element, `aria-invalid="true"`, and a `.kashira-field-error`
 *             helper with a stable id appended to `aria-describedby`.
 *   cleared → all of the above removed, aria-describedby restored to whatever
 *             the markup had before.
 *
 * Timing is the spec's: validate on BLUR, RE-validate on INPUT, and on a failed
 * submit move focus to the first invalid field. Field errors NEVER toast.
 *
 * Callers that already ship a live region for their copy (the coupon and
 * pincode message paragraphs) pass it in as `node`, so the message keeps living
 * in one place instead of being duplicated into a second helper.
 *
 * Exposed as window.KashiraFieldState because the consuming controllers ship as
 * separate section assets. This file is head-deferred in theme.liquid and they
 * are body-deferred, so it is always evaluated first.
 *
 * Reduced motion: no shake and no translate — the error border, the tint and
 * the helper carry the state on their own.
 */
(() => {
  const CONTROL = 'input, select, textarea';
  const SHAKE_MS = 300;
  const REDUCED = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Per-field bookkeeping keyed by the element itself: the border element and
     the helper node are elements, and none of it should survive a section
     re-render that swaps the DOM out from under us. */
  const state = new WeakMap();
  let seq = 0;

  /* ------------------------------------------------------------------ lookup */

  function control(el) {
    if (!el || typeof el.matches !== 'function' || !el.matches(CONTROL)) return null;
    if (el.disabled || el.willValidate === false) return null;
    if (el.type === 'hidden' || el.type === 'submit' || el.type === 'button' || el.type === 'reset') return null;
    if (el.closest('[hidden]')) return null;
    return el;
  }

  /* Dawn draws a field's border on its wrapper's `::after`, not on the control,
     and that overlay sits above the input — an error ring painted on the input
     is covered by it exactly. So the error state has to land on whichever
     element genuinely draws the border for this surface. */
  function borderTarget(field) {
    return field.closest('.field, .select, .kashira-pincode__row') || field.parentElement;
  }

  function bag(extra) {
    return Object.assign({}, window.kashiraFeedbackStrings, extra);
  }

  /**
   * Copy order: a per-field `data-kashira-error` override in the markup, then
   * the localized bag Liquid hands us, then the browser's own
   * `validationMessage`. The browser string is a last resort, never a default —
   * it is written in the BROWSER's language, not the storefront's.
   */
  function messageFor(field, strings) {
    if (field.dataset.kashiraError) return field.dataset.kashiraError;
    const t = bag(strings);
    const v = field.validity;
    if (v.valueMissing) return t.required || field.validationMessage || '';
    if (v.typeMismatch && field.type === 'email') return t.email || field.validationMessage || '';
    return t.invalid || field.validationMessage || t.required || '';
  }

  /* ------------------------------------------------------------------ motion */

  function shake(el) {
    if (!el || REDUCED()) return;
    el.classList.remove('kashira-shake');
    void el.offsetWidth; // reflow, so a repeat rejection re-runs the 300ms shake
    el.classList.add('kashira-shake');
    const done = (event) => {
      if (event && event.target !== el) return;
      clearTimeout(timer);
      el.removeEventListener('animationend', done);
      el.classList.remove('kashira-shake');
    };
    // animationend is not guaranteed (throttled frames, an interrupted or
    // restarted animation); a missed event must not strand the class or leak
    // the listener.
    const timer = setTimeout(done, SHAKE_MS + 120);
    el.addEventListener('animationend', done);
  }

  /* ------------------------------------------------------------------ errors */

  /**
   * The helper belongs BELOW the border box, never inside it: Dawn draws the
   * border on the wrapper's full-height `::after`, so a helper appended into
   * `.field` ends up enclosed by both the border and the error tint. Insert it
   * after the border element instead; where that parent lays its children out
   * in a row, `kashira-field-error--span` is the hook that gives the helper its
   * own line.
   */
  function place(node, target) {
    const host = target && target.parentElement;
    if (!host) {
      if (target) target.appendChild(node);
      return;
    }
    if (/flex|grid/.test(getComputedStyle(host).display)) node.classList.add('kashira-field-error--span');
    host.insertBefore(node, target.nextSibling);
  }

  function clear(field) {
    if (!field) return;
    field.removeAttribute('aria-invalid');
    field.classList.remove('kashira-field-invalid');

    const s = state.get(field);
    if (!s) return;
    state.delete(field);

    if (s.target) s.target.classList.remove('kashira-field-invalid', 'kashira-shake');
    if (s.node) {
      // Only nodes we created are removed — a caller-owned live region has to
      // survive so it can carry the success copy next.
      if (s.owned) s.node.remove();
      else s.node.classList.remove('kashira-field-error');
    }
    if (s.wired) {
      if (s.describedby) field.setAttribute('aria-describedby', s.describedby);
      else field.removeAttribute('aria-describedby');
    }
  }

  /**
   * options:
   *   target   — element that draws the border (defaults to the Dawn wrapper)
   *   node     — an existing message element to use as the helper
   *   message  — copy to write; `null` means "classes only", for server-rendered
   *              errors whose copy is already in the form-level banner
   *   shake    — false to suppress the 300ms shake (re-marking on blur)
   *   strings  — a localized bag layered over window.kashiraFeedbackStrings
   */
  function invalid(field, options = {}) {
    if (!field) return;
    clear(field); // idempotent: never stack two helpers on one field

    const target = options.target || borderTarget(field);
    field.setAttribute('aria-invalid', 'true');
    field.classList.add('kashira-field-invalid');
    if (target) target.classList.add('kashira-field-invalid');

    const s = { target, node: null, owned: false, wired: false, custom: options.message !== undefined };
    state.set(field, s);

    if (options.message !== null) {
      let node = options.node || null;
      if (node) {
        node.classList.add('kashira-field-error');
      } else {
        node = document.createElement('span');
        node.className = 'kashira-field-error';
        s.owned = true;
        place(node, target);
      }
      node.setAttribute('data-kashira-field-error', '');
      if (!node.id) node.id = `${field.id || `KashiraField-${(seq += 1)}`}-kashira-error`;
      // A caller-owned node with no message writes its own copy (the pincode
      // result paragraph animates its reveal around the text).
      if (s.owned || options.message !== undefined) {
        node.textContent = options.message !== undefined ? options.message : messageFor(field, options.strings);
      }
      s.node = node;

      // Keep any hint the markup already pointed at and append ours, so screen
      // readers still get the original alongside the error.
      const prev = field.getAttribute('aria-describedby') || '';
      s.describedby = prev;
      s.wired = true;
      field.setAttribute('aria-describedby', prev ? `${prev} ${node.id}` : node.id);
    }

    if (options.shake !== false) shake(target);
  }

  /* -------------------------------------------------------------- validation */

  function revalidate(field, options) {
    if (field.checkValidity()) {
      clear(field);
      return;
    }
    const s = state.get(field);
    if (!s || !s.node || s.custom) return;
    s.node.textContent = messageFor(field, options && options.strings);
  }

  /** Blur + input contract, without taking over the form's submit. */
  function bindLive(form, options = {}) {
    if (form.dataset.kashiraLive === 'bound') return;
    form.dataset.kashiraLive = 'bound';

    // Spec: RE-validate on input. An errored field clears the moment it becomes
    // valid and keeps its copy current while it is still wrong; typing never
    // raises a new error.
    const live = (event) => {
      const field = control(event.target);
      if (!field) return;
      // Sticky, not a diff against the value at focus — a field typed into and
      // then emptied again is still a field the shopper has been in.
      field.dataset.kashiraTouched = 'true';
      if (state.has(field)) revalidate(field, options);
    };
    form.addEventListener('input', live);
    form.addEventListener('change', live);

    // Spec: validate on blur — but only for a field the shopper has actually
    // been in. Tabbing through an untouched form must not paint every required
    // box red before they have had a chance to fill it.
    form.addEventListener('focusout', (event) => {
      const field = control(event.target);
      if (!field) return;
      const first = !state.has(field);
      if (field.dataset.kashiraTouched !== 'true' && first) return;
      if (field.checkValidity()) clear(field);
      // The shake plays once on the transition INTO error, not on every blur
      // that leaves the field still wrong.
      else invalid(field, Object.assign({}, options, { shake: first }));
    });
  }

  /** Clears, re-marks and focuses. Returns true when the form is valid. */
  function check(form, options = {}) {
    const fields = Array.from(form.querySelectorAll(CONTROL))
      .map(control)
      .filter(Boolean);
    const bad = fields.filter((field) => !field.checkValidity());
    fields.forEach((field) => {
      if (!bad.includes(field)) clear(field);
    });
    if (!bad.length) return true;
    bad.forEach((field) => invalid(field, options));
    bad[0].focus();
    return false;
  }

  function bindForm(form, options) {
    if (form.dataset.kashiraFeedback === 'bound') return;
    form.dataset.kashiraFeedback = 'bound';
    // Native bubbles are not a theme surface and cannot be styled; the inline
    // helper replaces them.
    form.setAttribute('novalidate', 'novalidate');
    bindLive(form, options);
    form.addEventListener('submit', (event) => {
      if (!check(form, options)) event.preventDefault();
    });
  }

  window.KashiraFieldState = { invalid, clear, shake, check, bindLive, bindForm, messageFor, control };

  /* --------------------------------------------------------- inline success */

  /**
   * Share copy. Dawn already swaps the copy button for the close button and
   * reveals `.share-button__message` — the control genuinely is replaced. What
   * was missing is the 300ms crossfade, so this only adds the state class.
   * No DOM restructuring: Dawn's
   * `.share-button__close:not(.hidden) + .share-button__copy` adjacency and
   * share.js both depend on the existing node order.
   *
   * share.js unhides the message inside the async clipboard promise, so we
   * watch for `.hidden` coming off rather than racing it from the click
   * handler — otherwise the element goes display:none -> visible with no
   * transition and the fade is skipped. The confirmation persists until the
   * disclosure closes (share.js clears it), so there is no 1.2s revert here.
   */
  function bindShareCopy() {
    document.querySelectorAll('share-button').forEach((host) => {
      const fallback = host.querySelector('.share-button__fallback');
      const message = host.querySelector('.share-button__message');
      if (!fallback || !message || message.dataset.kashiraSwap === 'bound') return;
      message.dataset.kashiraSwap = 'bound';

      new MutationObserver(() => {
        if (message.classList.contains('hidden')) {
          fallback.classList.remove('kashira-share-copied');
        } else if (!fallback.classList.contains('kashira-share-copied')) {
          // next frame, so the un-hidden element has a rendered opacity:0 to
          // transition away from
          requestAnimationFrame(() => fallback.classList.add('kashira-share-copied'));
        }
      }).observe(message, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /**
   * Chrome that a success plate has replaced is gone for the mouse (the swap
   * fades it and drops pointer-events) but not for the keyboard: it stays in
   * the tab order and in the a11y tree, so tab lands on a control nobody can
   * see. `inert` covers both in current browsers; the explicit disable +
   * tabindex is what makes `aria-hidden` legitimate where it does not, since
   * aria-hidden over a focusable control is itself a violation.
   */
  function retire(row) {
    if (!row || row.dataset.kashiraRetired === 'true') return;
    row.dataset.kashiraRetired = 'true';
    row.querySelectorAll('input, select, textarea, button, a[href], [tabindex]').forEach((el) => {
      el.tabIndex = -1;
      if ('disabled' in el) el.disabled = true;
    });
    row.setAttribute('inert', '');
    row.setAttribute('aria-hidden', 'true');
  }

  /**
   * Newsletter. Two renders and one live path share one crossfade: on the
   * `?customer_posted=true` render Shopify prints the success <h3> unhidden
   * and it swaps over the field row on load; the AJAX path below unhides the
   * same <h3> and runs the same swap. Per spec 17 the success genuinely
   * replaces the control instead of stacking under it.
   */
  function swapToSuccess(form, success, fieldRow) {
    form.dataset.kashiraSwap = 'bound';
    success.hidden = false;
    form.classList.add('kashira-newsletter-swap');
    requestAnimationFrame(() => form.classList.add('is-swapped'));
    retire(fieldRow);
  }

  /**
   * Dawn's stock subscribe is a full navigation: the customer form posts to
   * /contact#ContactFooter and every outcome — error or success — strands the
   * shopper on the contact page. Valid submits are posted with fetch instead
   * and confirmed in place. Only two outcomes are asserted: the redirect that
   * carries `customer_posted=true` (proof it registered) and an error
   * re-render whose copy Shopify printed into the form's own message. Anything
   * unverifiable — Cloudflare challenge, network failure, unreadable body —
   * falls back to the classic navigation submit so no outcome is swallowed.
   */
  function bindNewsletterAjax(form, success, fieldRow) {
    if (form.dataset.kashiraAjax === 'bound') return;
    form.dataset.kashiraAjax = 'bound';
    form.addEventListener('submit', (event) => {
      // bindForm's listener registered first, so the invalid path has already
      // been prevented (and painted) by the time this runs.
      if (event.defaultPrevented) return;
      event.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const button = form.querySelector('button[type="submit"]');
      if (button) button.disabled = true;
      // form.submit() fires no submit event, so the fallback cannot re-enter.
      const fallback = () => form.submit();
      // A hung request would otherwise strand the button disabled forever.
      const timeout = AbortSignal.timeout ? AbortSignal.timeout(8000) : undefined;
      fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        signal: timeout,
      })
        .then(async (res) => {
          if (res.ok && new URL(res.url).searchParams.get('customer_posted') === 'true') {
            swapToSuccess(form, success, fieldRow);
            success.focus();
            return;
          }
          if (res.ok) {
            // Error re-render: the same form comes back with Shopify's copy in
            // its error <small>. The :not() matters — the success <h3> is in
            // every render now and also carries .form__message.
            const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
            const message = doc.querySelector(
              '.newsletter-form .form__message:not(.newsletter-form__message--success)'
            );
            if (message && input) {
              if (button) button.disabled = false;
              invalid(input, { message: message.textContent.replace(/\s+/g, ' ').trim() });
              input.focus();
              return;
            }
          }
          fallback();
        })
        .catch(fallback);
    });
  }

  function bindNewsletter() {
    document.querySelectorAll('.newsletter-form').forEach((form) => {
      bindForm(form);
      const success = form.querySelector('.newsletter-form__message--success');
      const fieldRow = form.querySelector('.newsletter-form__field-wrapper');
      if (!success || !fieldRow) return;
      bindNewsletterAjax(form, success, fieldRow);
      if (form.dataset.kashiraSwap === 'bound') return;
      // The success <h3> is in the markup on every render, hidden except on
      // the posted render — only that one swaps on load.
      if (success.hidden) return;
      swapToSuccess(form, success, fieldRow);
    });
  }

  function init() {
    document
      .querySelectorAll(
        '.newsletter-form, #customer_login, #create_customer, #recover_customer_password, #reset_customer_password, #activate_customer_password'
      )
      .forEach((form) => bindForm(form));

    // Server-rendered Shopify errors: mirror the border + shake contract on
    // load. `message: null` because the form-level banner already prints the
    // copy — the spec's one-signal rule covers saying it twice as much as it
    // covers a toast and a morph together.
    document.querySelectorAll('.form__message--error, .newsletter-form__message--error').forEach((banner) => {
      const form = banner.closest('form');
      const field = form && form.querySelector('[aria-invalid="true"], input[type="email"], input[type="password"]');
      if (field) invalid(field, { message: null });
    });

    bindShareCopy();
    bindNewsletter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
